import React from 'react'
import { useLatest } from 'react-use'
import type { InitOptions } from '@tinymce/tinymce-react/lib/cjs/main/ts/components/Editor'
import type { RichTextInputProps } from '@ztechapps/webui.input'
import type { SetOptional } from 'type-fest'
import { RichTextInput } from '@ztechapps/webui.input'
import { useFileUpload } from '@ztechapps/common.hook'
import { useChannelContext } from './Context'

type ImageUploadHandler = NonNullable<InitOptions['images_upload_handler']>

export const RichTextChatInput = ({
  height = '30vh',
  ...props
}: SetOptional<RichTextInputProps, 'onChange' | 'value'>) => {
  const { message = '', setMessage, chatInputRef, status, setPendingImageUploads } = useChannelContext()
  const [uploadFiles] = useFileUpload()
  const messageRef = useLatest(message)

  const uploadImage: ImageUploadHandler = async (blobInfo) => {
    setPendingImageUploads((count) => count + 1)
    // TinyMCE swaps the image src and fires change in promise callbacks after this promise
    // settles, with no timer on that path (tinymce.js:20146-20173, 21130-21138 against
    // 20092-20291) — so the deferred read-back runs after the draft already holds the stored
    // address. A removed image fires no change (tinymce.js:20170-20173), hence the read-back.
    const settle = () => {
      const html = chatInputRef.current?.getContent()
      if (typeof html === 'string' && html !== messageRef.current) setMessage(html)
      setPendingImageUploads((count) => count - 1)
    }
    try {
      return (await uploadFiles(blobInfo.blob())).uri
    } catch {
      throw Object.assign(new Error('The image could not be uploaded.'), { remove: true })
    } finally {
      setTimeout(settle)
    }
  }

  return (
    <RichTextInput
      editorRef={chatInputRef}
      onChange={setMessage}
      value={message}
      height={height}
      disabled={status.editorLocked}
      {...props}
      init={{ ...props.init, images_upload_handler: uploadImage }}
    />
  )
}
